import React, { useState, useEffect, useCallback } from "react";
import {
  FaUser,
  FaPhone,
  FaHome,
  FaCity,
  FaMapPin,
  FaUtensils,
  FaEnvelope,
  FaGlobe,
  FaIdCard,
} from "react-icons/fa";
import FormField from "./FormField";
import ProfileChallenges from "./ProfileChallenges";
import NotificationSummary from "./NotificationSummary";
import "./css/accountManage.css";
import "./css/profileChallenges.css";
import "./css/notificationSummary.css";
import "./css/mobile.css";
import translations from "../translations.json";
import ProfileImage from "./ProfileImage";
import useApi from "../../../Hooks/useApi";
import useTranslations from "../../../Hooks/useTranslations";
import { useAutoSave } from "../../../Context/AutoSaveContext";
import { changeLanguage } from "../../../Redux/middleware/languageMiddleware";
import { useSelector } from "react-redux";
import { selectCurrentLanguage } from "../../../Redux/reducers/languageReducer";

import { useUpdateAccountMutation } from "../../../store/accountApi";

const AccountManage = ({
  accountData,
  setAccountData,
  triggerNotification,
  refetchAccountData,
}) => {
  const t = useTranslations(translations, 'accountManage');
  const fullTranslations = useTranslations(translations); // For child components
  const preferredLanguage = useSelector(selectCurrentLanguage);
  const api = useApi();

  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation();

  const hour = new Date().getHours();
  let greetingWord, greetingEmoji;
  if (hour < 6) {
    greetingWord = t.greetings.night;
    greetingEmoji = "🌙";
  } else if (hour < 12) {
    greetingWord = t.greetings.morning;
    greetingEmoji = "👋";
  } else if (hour < 18) {
    greetingWord = t.greetings.afternoon;
    greetingEmoji = "☀️";
  } else {
    greetingWord = t.greetings.evening;
    greetingEmoji = "😊";
  }

  let greeting = greetingWord;
  if (accountData && accountData.first_name) {
    greeting += ` ${accountData.first_name}!`;
  }
  greeting += ` ${greetingEmoji}`;

  const [formData, setFormData] = useState({
    first_name: "",
    last_name: "",
    email: "",
    phone_number: "",
    street: "",
    house_number: "",
    city: "",
    postal_code: "",
    country: "",
    vat_number: "",
    bio: "",
    imageId: "",
    profile_image_url: "",
    restaurant_name: "",
    tripadvisor_review_link: "",
    google_review_link: "",
    preferred_language: localStorage.getItem("preferredLanguage") || "nl",
  });

  const [loading, setLoading] = useState(false);
  const [isFormDirty, setIsFormDirty] = useState(false);
  const [hasInitialized, setHasInitialized] = useState(false);

  const initializeFormData = useCallback(() => {
    if (accountData && !hasInitialized) {
      setFormData({
        first_name: accountData.first_name || "",
        last_name: accountData.last_name || "",
        email: accountData.email || "",
        phone_number: accountData.phone_number || "",
        street: accountData.street || "",
        house_number: accountData.house_number || "",
        city: accountData.city || "",
        postal_code: accountData.postal_code || "",
        country: accountData.country || "",
        vat_number: accountData.vat_number || "",
        bio: accountData.bio || "",
        imageId: accountData.imageId || "",
        profile_image_url: accountData.profile_image_url || "",
        restaurant_name: accountData.restaurant_name || "",
        tripadvisor_review_link: accountData.tripadvisor_review_link || "",
        google_review_link: accountData.google_review_link || "",
        preferred_language: localStorage.getItem("preferredLanguage") || "nl",
      });

      if (accountData.restaurant_name) {
        localStorage.setItem("restaurantName", accountData.restaurant_name);
      }

      setHasInitialized(true);
      setIsFormDirty(false);
    }
  }, [accountData, hasInitialized]);

  useEffect(() => {
    initializeFormData();
  }, [initializeFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsFormDirty(true);

    if (name === "preferred_language") {
      changeLanguage(value);
    }
  };

  const handleAvatarSelect = (avatarKey) => {
    setFormData((prev) => ({ ...prev, imageId: avatarKey, profile_image_url: "" }));
    setIsFormDirty(true);
    localStorage.setItem("selectedAvatar", avatarKey);
    localStorage.removeItem("profileImageUrl");
  };

  const handleCustomImageUpload = (imageUrl) => {
    setFormData((prev) => ({ ...prev, profile_image_url: imageUrl, imageId: "" }));
    setIsFormDirty(true);
    localStorage.setItem("profileImageUrl", imageUrl);
    localStorage.removeItem("selectedAvatar");
  };

  const handleSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    if (!isFormDirty) return; // nothing to do

    setLoading(true);
    try {
      let updatedData;

      if (accountData && accountData.storedNumber) {
        try {
          updatedData = await updateAccount(formData).unwrap();
          console.log(
            "[AccountManage] RTKQ updateAccount success:",
            updatedData
          );
        } catch (rtkErr) {
          console.error(
            "[AccountManage] RTKQ updateAccount failed, fallback:",
            rtkErr
          );
          try {
            updatedData = await api.patch(
              `${window.baseDomain}api/account`,
              formData
            );
            console.log(
              "[AccountManage] Fallback useApi.patch success:",
              updatedData
            );
          } catch (fallbackErr) {
            console.error(
              "[AccountManage] Fallback useApi.patch failed:",
              fallbackErr
            );
            triggerNotification(t.notifications.errorSaving, "error");
            return;
          }
        }
        triggerNotification(t.notifications.accountUpdated, "success");
      } else {
        try {
          updatedData = await api.post(
            `${window.baseDomain}api/account`,
            formData
          );
          console.log(
            "[AccountManage] Created account via useApi.post:",
            updatedData
          );
          triggerNotification(t.notifications.dataAdded, "success");
        } catch (createErr) {
          console.error(
            "[AccountManage] useApi.post create failed:",
            createErr
          );
          triggerNotification(t.notifications.errorSaving, "error");
          return;
        }
      }

      if (updatedData) {
        if (setAccountData) {
          setAccountData(updatedData);
        }

        changeLanguage(formData.preferred_language);
        if (formData.restaurant_name) {
          localStorage.setItem("restaurantName", formData.restaurant_name);
        }
        if (formData.imageId) {
          localStorage.setItem("selectedAvatar", formData.imageId);
        }

        setIsFormDirty(false);
      }
    } catch (error) {
      console.error("[AccountManage] handleSave fatal error:", error);
      triggerNotification(t.notifications.errorSaving, "error");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      // Call server to clear the refresh token cookie
      await api.post(window.baseDomain + 'api/auth/jwt-logout/', {}, { withCredentials: true });
    } catch (err) {
      // Continue with logout even if server call fails
      console.error('Logout server call failed:', err);
    }
    localStorage.clear();
    window.location.reload();
  };

  const avatarTranslations = {
    selectAvatar: (t.avatar && t.avatar.selectAvatar) || "Click to select avatar",
    chooseAvatar: (t.avatar && t.avatar.chooseAvatar) || "Choose an Avatar",
    uploadCustom: (t.avatar && t.avatar.uploadCustom) || "Upload Custom Image",
    uploading: (t.avatar && t.avatar.uploading) || "Uploading...",
    orChooseAvatar: (t.avatar && t.avatar.orChooseAvatar) || "or choose an avatar",
    invalidFileType: (t.avatar && t.avatar.invalidFileType) || "Please select an image file",
    fileTooLarge: (t.avatar && t.avatar.fileTooLarge) || "File size must be less than 5MB",
    uploadError: (t.avatar && t.avatar.uploadError) || "Failed to upload image. Please try again.",
  };

  const isSaving = loading || isUpdating;
  const saveDisabled = isSaving || !isFormDirty;

  // Auto-save when navigating away with unsaved changes
  useAutoSave(handleSave, isFormDirty);

  return (
    <div className="profile-page">
      <h2 className="account-manage-title">{greeting}</h2>

      <ProfileImage
        selectedAvatarId={formData.imageId}
        onAvatarSelect={handleAvatarSelect}
        customImageUrl={formData.profile_image_url}
        onCustomImageUpload={handleCustomImageUpload}
        translations={avatarTranslations}
      />

      <NotificationSummary
        translations={fullTranslations}
      />

      <ProfileChallenges
        formData={formData}
        translations={fullTranslations}
      />

      <div className="account-manage-container">
        <form className="account-manage-form" onSubmit={handleSave}>
          <div className="form-section">
            <h3 className="section-title" title="personal-info">
              {t.sections.personalInfo}
            </h3>
            <FormField
              label={t.formFields.firstName.label}
              name="first_name"
              placeholder={t.formFields.firstName.placeholder}
              value={formData.first_name}
              onChange={handleChange}
              icon={FaUser}
            />
            <FormField
              label={t.formFields.lastName.label}
              name="last_name"
              placeholder={t.formFields.lastName.placeholder}
              value={formData.last_name}
              onChange={handleChange}
              icon={FaUser}
            />
            <FormField
              label={t.formFields.email.label}
              name="email"
              type="email"
              placeholder={t.formFields.email.placeholder}
              value={formData.email}
              onChange={handleChange}
              icon={FaEnvelope}
            />
            <FormField
              label={t.formFields.phoneNumber.label}
              name="phone_number"
              type="tel"
              placeholder={t.formFields.phoneNumber.placeholder}
              value={formData.phone_number}
              onChange={handleChange}
              icon={FaPhone}
            />
          </div>

          <div className="form-section">
            <h3 className="section-title" title="address-info">
              {t.sections.addressInfo}
            </h3>
            <div className="form-row">
              <FormField
                label={t.formFields.street.label}
                name="street"
                placeholder={t.formFields.street.placeholder}
                value={formData.street}
                onChange={handleChange}
                icon={FaHome}
                halfWidth
              />
              <FormField
                label={t.formFields.houseNumber.label}
                name="house_number"
                placeholder={t.formFields.houseNumber.placeholder}
                value={formData.house_number}
                onChange={handleChange}
                icon={FaHome}
                halfWidth
              />
            </div>
            <div className="form-row">
              <FormField
                label={t.formFields.city.label}
                name="city"
                placeholder={t.formFields.city.placeholder}
                value={formData.city}
                onChange={handleChange}
                icon={FaCity}
                halfWidth
              />
              <FormField
                label={t.formFields.postalCode.label}
                name="postal_code"
                placeholder={t.formFields.postalCode.placeholder}
                value={formData.postal_code}
                onChange={handleChange}
                icon={FaMapPin}
                halfWidth
              />
            </div>
            <FormField
              label={t.formFields.country.label}
              name="country"
              placeholder={t.formFields.country.placeholder}
              value={formData.country}
              onChange={handleChange}
              icon={FaGlobe}
            />
          </div>

          <div className="form-section">
            <h3 className="section-title" title="business-info">
              {t.sections.businessInfo}
            </h3>
            <FormField
              label={t.formFields.restaurantName.label}
              name="restaurant_name"
              placeholder={t.formFields.restaurantName.placeholder}
              value={formData.restaurant_name}
              onChange={handleChange}
              icon={FaUtensils}
            />
            <FormField
              label={t.formFields.vatNumber.label}
              name="vat_number"
              placeholder={t.formFields.vatNumber.placeholder}
              value={formData.vat_number}
              onChange={handleChange}
              icon={FaIdCard}
            />
            <FormField
              label={t.formFields.tripadvisorLink.label}
              name="tripadvisor_review_link"
              placeholder={t.formFields.tripadvisorLink.placeholder}
              value={formData.tripadvisor_review_link}
              onChange={handleChange}
              icon={FaUtensils}
            />
            <FormField
              label={t.formFields.googleReviewLink.label}
              name="google_review_link"
              placeholder={t.formFields.googleReviewLink.placeholder}
              value={formData.google_review_link}
              onChange={handleChange}
              icon={FaUtensils}
            />
          </div>

          <div className="button-row">
            <button type="submit" className="button-style-3" disabled={saveDisabled}>
              {isSaving ? t.buttons.saving : t.buttons.save}
            </button>
            <button
              type="button"
              className="button-style-3 button-style-logout"
              onClick={handleLogout}
            >
              {t.buttons.logout}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AccountManage;
